import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { DataModeBadge } from "@/components/DataModeBadge";
import {
  Users,
  TrendingUp,
  ChevronDown,
  Calendar as CalendarIcon,
  CheckCircle,
  Clock,
  XCircle,
  AlertCircle,
  Target,
  HandCoins,
  Receipt,
  Scale,
} from "lucide-react";
import { NavigationMetricCardAlt } from "@/components/NavigationMetricCardAlt";
import { useNavigate } from "react-router-dom";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { generateMockCases, generateMockTeamPerformance } from "@/lib/mock-data/collections-mock-data";
import { DashboardSelector } from "@/components/dashboard";
import { type DateRange } from "react-day-picker";
import { format } from "date-fns";

// Custom Tooltip Component
const CustomTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-3">
        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
          Week {payload[0].payload.week}
        </p>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          £{payload[0].value.toLocaleString()} Collected
        </p>
      </div>
    );
  }
  return null;
};

export default function CollectionsDashboard() {
  const navigate = useNavigate();
  const [isLoading] = useState(false);
  const [cases] = useState(generateMockCases());
  const [teamPerformance] = useState(generateMockTeamPerformance());
  const [selectedTeamMember, setSelectedTeamMember] = useState<string>("all");
  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: new Date(new Date().setDate(new Date().getDate() - 30)),
    to: new Date(),
  });
  
  // Filter cases by selected team member and date range
  const filteredCases = cases.filter(c => {
    // Filter by team member
    const matchesTeamMember = selectedTeamMember === "all" || c.assignedTo === selectedTeamMember;
    
    // Filter by date range (assuming cases have a createdDate or similar field)
    // For now, we'll just apply team member filter since mock data may not have dates
    // In production, you would add: && c.createdDate >= dateRange.from && c.createdDate <= dateRange.to
    
    return matchesTeamMember;
  });
  
  // Filter team performance by selected team member
  const filteredTeamPerformance = selectedTeamMember === "all"
    ? teamPerformance
    : teamPerformance.filter(member => member.name === selectedTeamMember);
  
  // Calculate dashboard metrics
  const dashboardData = {
    teamPerformance: filteredTeamPerformance,
    weeklyTrends: [
      { week: 1, collected: 125000 },
      { week: 2, collected: 138000 },
      { week: 3, collected: 142000 },
      { week: 4, collected: 135000 },
      { week: 5, collected: 148000 },
      { week: 6, collected: 152000 },
      { week: 7, collected: 145000 },
      { week: 8, collected: 158000 },
      { week: 9, collected: 162000 },
      { week: 10, collected: 155000 },
      { week: 11, collected: 168000 },
      { week: 12, collected: 172000 },
      { week: 13, collected: 178000 },
    ],
    channelPerformance: [
      { channel: 'Email', responseRate: 68, totalSent: 450, responses: 306 },
      { channel: 'Phone', responseRate: 82, totalSent: 320, responses: 262 },
      { channel: 'SMS', responseRate: 45, totalSent: 280, responses: 126 },
      { channel: 'Portal', responseRate: 91, totalSent: 150, responses: 137 },
    ],
    casesByStatus: [
      { 
        status: 'Active', 
        count: filteredCases.filter(c => c.status === 'Active').length, 
        totalValue: filteredCases.filter(c => c.status === 'Active').reduce((sum, c) => sum + c.amount, 0) 
      },
      { 
        status: 'Pending', 
        count: filteredCases.filter(c => c.status === 'On Hold').length, 
        totalValue: filteredCases.filter(c => c.status === 'On Hold').reduce((sum, c) => sum + c.amount, 0) 
      },
      { 
        status: 'Resolved', 
        count: filteredCases.filter(c => c.status === 'Closed').length, 
        totalValue: filteredCases.filter(c => c.status === 'Closed').reduce((sum, c) => sum + c.amount, 0) 
      },
      { 
        status: 'Disputed', 
        count: filteredCases.filter(c => c.status === 'Promised').length, 
        totalValue: filteredCases.filter(c => c.status === 'Promised').reduce((sum, c) => sum + c.amount, 0) 
      },
    ],
  };

  return (
    <div className="p-6 space-y-6">
      {/* Dashboard Toggle Buttons */}
      <div className="flex items-center justify-between">
        <DashboardSelector />
      </div>

      {/* Header with Team Member Filter */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="text-3xl font-bold tracking-tight">Collections Dashboard</h1>
            <DataModeBadge />
          </div>
          <p className="text-muted-foreground">Deep team analytics and trend analysis</p>
        </div>
        
        {/* Filters */}
        <div className="flex items-center gap-4">
          {/* Date Range Filter */}
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="w-[280px] justify-start text-left font-normal">
                <CalendarIcon className="mr-2 h-4 w-4" />
                {dateRange?.from ? (
                  dateRange.to ? (
                    <>
                      {format(dateRange.from, "LLL dd, y")} - {format(dateRange.to, "LLL dd, y")}
                    </>
                  ) : (
                    format(dateRange.from, "LLL dd, y")
                  )
                ) : (
                  <span>Pick a date range</span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar
                initialFocus
                mode="range"
                defaultMonth={dateRange?.from}
                selected={dateRange}
                onSelect={setDateRange}
                numberOfMonths={2}
              />
            </PopoverContent>
          </Popover>

          {/* Team Member Filter */}
          <div className="flex items-center gap-2">
            <Users className="h-4 w-4 text-muted-foreground" />
            <Select value={selectedTeamMember} onValueChange={setSelectedTeamMember}>
              <SelectTrigger className="w-[200px]">
                <SelectValue placeholder="Filter by team member" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Team Members</SelectItem>
                {teamPerformance.map((member) => (
                  <SelectItem key={member.id} value={member.name}>
                    {member.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Dashboard Content */}
      <div className="space-y-4">
          {/* Cases by Status Cards */}
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <NavigationMetricCardAlt
              title="Active"
              value={dashboardData.casesByStatus[0].count.toString()}
              secondaryValue={`£${dashboardData.casesByStatus[0].totalValue.toLocaleString()} total value`}
              icon={Target}
              variant="info"
              onClick={() => navigate('/collections/queue')}
            />
            <NavigationMetricCardAlt
              title="Pending"
              value={dashboardData.casesByStatus[1].count.toString()}
              secondaryValue={`£${dashboardData.casesByStatus[1].totalValue.toLocaleString()} total value`}
              icon={Receipt}
              variant="info"
              onClick={() => navigate('/transactions')}
            />
            <NavigationMetricCardAlt
              title="Resolved"
              value={dashboardData.casesByStatus[2].count.toString()}
              secondaryValue={`£${dashboardData.casesByStatus[2].totalValue.toLocaleString()} total value`}
              icon={CheckCircle}
              variant="info"
              onClick={() => navigate('/collections/cases?status=resolved')}
            />
            <NavigationMetricCardAlt
              title="Disputed"
              value={dashboardData.casesByStatus[3].count.toString()}
              secondaryValue={`£${dashboardData.casesByStatus[3].totalValue.toLocaleString()} total value`}
              icon={Scale}
              variant="info"
              onClick={() => navigate('/disputes/all')}
            />
          </div>

          {/* Weekly Collections Trend */}
          <Card>
            <CardHeader>
              <CardTitle>Weekly Collections Trend</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {/* Summary Metrics */}
                <div className="grid grid-cols-3 gap-4 pb-4 border-b">
                  <div>
                    <div className="text-sm text-muted-foreground">Total Collected</div>
                    <div className="text-2xl font-bold">
                      £{dashboardData.weeklyTrends.reduce((sum, w) => sum + w.collected, 0).toLocaleString()}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Average per Week</div>
                    <div className="text-2xl font-bold">
                      £{Math.round(dashboardData.weeklyTrends.reduce((sum, w) => sum + w.collected, 0) / dashboardData.weeklyTrends.length).toLocaleString()}
                    </div>
                  </div>
                  <div>
                    <div className="text-sm text-muted-foreground">Trend</div>
                    <div className="text-2xl font-bold text-green-600 flex items-center gap-1">
                      <TrendingUp className="h-5 w-5" />
                      +8.2%
                    </div>
                  </div>
                </div>

                {/* Line Chart */}
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={dashboardData.weeklyTrends} margin={{ left: 10, right: 10 }}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis 
                        dataKey="week" 
                        className="text-xs"
                        tick={{ fill: 'hsl(var(--muted-foreground))' }}
                        interval={0}
                        padding={{ left: 20, right: 20 }}
                        tickFormatter={(value) => value === 1 ? 'Week 1' : value.toString()}
                      />
                      <YAxis 
                        className="text-xs"
                        tick={{ fill: 'hsl(var(--muted-foreground))' }}
                        tickFormatter={(value) => `${(value / 1000).toFixed(0)}K`}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Line 
                        type="monotone" 
                        dataKey="collected" 
                        stroke="#0d9488" 
                        strokeWidth={3}
                        dot={{ fill: '#0d9488', r: 4 }}
                        activeDot={{ r: 6 }}
                        connectNulls={true}
                        isAnimationActive={false}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Collection Efficiency and Team Velocity Cards */}
          <div className="grid gap-4 md:grid-cols-2">
            {/* Collection Efficiency */}
            <Card>
              <CardHeader>
                <CardTitle>Collection Efficiency</CardTitle>
                <CardDescription>Contact-to-payment conversion</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div>
                    <div className="text-sm text-muted-foreground mb-2">Overall Conversion Rate</div>
                    <div className="text-3xl font-bold">64.2%</div>
                    <div className="text-xs text-green-600 flex items-center gap-1 mt-1">
                      <TrendingUp className="h-3 w-3" />
                      +2.3% vs last month
                    </div>
                  </div>
                  
                  <div className="space-y-3 pt-4 border-t">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Avg Touches per Collection</span>
                      <span className="text-sm font-medium">3.2</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">First Contact Success</span>
                      <span className="text-sm font-medium">28%</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Avg Days to Payment</span>
                      <span className="text-sm font-medium">14.3</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Team Velocity */}
            <Card>
              <CardHeader>
                <CardTitle>Team Velocity</CardTitle>
                <CardDescription>Cases resolved per week</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div>
                    <div className="text-sm text-muted-foreground mb-2">This Week</div>
                    <div className="text-3xl font-bold">58</div>
                    <div className="text-xs text-green-600 flex items-center gap-1 mt-1">
                      <TrendingUp className="h-3 w-3" />
                      +12 vs last week
                    </div>
                  </div>
                  
                  <div className="space-y-3 pt-4 border-t">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">4-Week Average</span>
                      <span className="text-sm font-medium">52</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Best Week</span>
                      <span className="text-sm font-medium">64</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Resolution Rate</span>
                      <span className="text-sm font-medium">78.5%</span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Team Member Performance</CardTitle>
              <CardDescription>Individual metrics and active workload</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                {dashboardData.teamPerformance.map((member) => (
                  <div key={member.id} className="space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-sm font-semibold text-primary">
                          {member.name
                            .split(" ")
                            .map((n) => n[0])
                            .join("")}
                        </div>
                        <div>
                          <p className="font-medium">{member.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {member.activeCases} active cases • {member.resolvedCases} resolved this month
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-semibold">£{member.totalCollected.toLocaleString()}</p>
                        <p className="text-sm text-muted-foreground">collected</p>
                      </div>
                    </div>
                    <div className="grid gap-2 md:grid-cols-3">
                      <div>
                        <div className="mb-1 flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Success Rate</span>
                          <span className="font-medium">{member.successRate}%</span>
                        </div>
                        <Progress value={member.successRate} className="h-2" />
                      </div>
                      <div>
                        <div className="mb-1 flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Avg Response Time</span>
                          <span className="font-medium">{member.avgResponseTime}h</span>
                        </div>
                        <Progress value={(24 - member.avgResponseTime) * 4} className="h-2" />
                      </div>
                      <div>
                        <div className="mb-1 flex items-center justify-between text-sm">
                          <span className="text-muted-foreground">Overdue Actions</span>
                          <Badge variant={member.overdueActions > 0 ? "destructive" : "secondary"}>
                            {member.overdueActions}
                          </Badge>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
      </div>
    </div>
  );
}
